import { BaseService, ServiceResponse } from './base.service';
import { PerformanceRecordsService } from './performance-records.service';
import { CourseLeaderboardService } from './course-leaderboard.service';
import { LocalLegendService } from './local-legend.service';
import type { 
  PerformanceZonesSummary,
  PerformanceAchievement,
  ProgressUpdate,
  RecordNotification
} from '../../types/performance-zones';

export class AchievementTrackingService extends BaseService {
  private performanceRecords: PerformanceRecordsService;
  private courseLeaderboard: CourseLeaderboardService;
  private localLegend: LocalLegendService;

  constructor() {
    super();
    this.performanceRecords = new PerformanceRecordsService();
    this.courseLeaderboard = new CourseLeaderboardService();
    this.localLegend = new LocalLegendService();
  }

  // Get complete Performance Zones summary for a user
  async getUserPerformanceZones(userId: string): Promise<ServiceResponse<PerformanceZonesSummary>> {
    try {
      // Read from the authoritative records tables (written by PerformanceRecordsService)
      const [holeRecordsResult, courseRecordsResult] = await Promise.all([
        this.performanceRecords.getHoleRecords(userId),
        this.performanceRecords.getCourseRecords(userId),
      ]);

      const holeRecords = holeRecordsResult.data || [];
      const courseRecords = courseRecordsResult.data || [];

      // Local legend: count rounds per course in last 90 days from solo_rounds
      const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
      const { data: recentRounds } = await this.supabase
        .from('solo_rounds')
        .select('course_id, completed_at, courses(name)')
        .eq('user_id', userId)
        .eq('status', 'completed')
        .gte('completed_at', ninetyDaysAgo);

      const courseRoundCounts = (recentRounds || []).reduce((acc, round) => {
        const courseId = round.course_id;
        const courseName = (round.courses as any)?.name || 'Unknown Course';
        if (!acc[courseId]) acc[courseId] = { course_id: courseId, course_name: courseName, count: 0 };
        acc[courseId].count++;
        return acc;
      }, {} as Record<string, { course_id: string; course_name: string; count: number }>);

      const localLegends = Object.values(courseRoundCounts).map(course => ({
        course_id: course.course_id,
        course_name: course.course_name,
        user_id: userId,
        round_count: course.count,
        is_legend: course.count >= 5,
        legend_status: course.count >= 5 ? 'Current Legend' : `${course.count} rounds`,
        achieved_at: new Date().toISOString()
      }));

      // Top-10: read from performance_rankings cache
      const { data: rankingRows } = await this.supabase
        .from('performance_rankings')
        .select('*')
        .eq('user_id', userId)
        .lte('ranking_position', 10);

      const top10Courses = (rankingRows || [])
        .filter(r => r.ranking_type === 'course')
        .map(r => ({
          id: r.id,
          course_id: r.course_id,
          course_name: r.course_name || 'Unknown Course',
          user_id: userId,
          ranking_position: r.ranking_position,
          total_score: r.score,
          record_type: r.record_type || 'scratch',
          achieved_at: r.achieved_at,
          user_name: 'You',
          gender: r.gender || 'overall'
        }));

      const summary: PerformanceZonesSummary = {
        hole_records: holeRecords,
        course_records: courseRecords,
        local_legend_courses: localLegends,
        top_10_holes: [],
        top_10_courses: top10Courses,
        recent_achievements: [],
        close_to_achievements: [],
        stats: {
          total_hole_records: holeRecords.length,
          total_course_records: courseRecords.length,
          total_local_legends: localLegends.filter(l => l.is_legend).length,
          total_top_10_finishes: top10Courses.length,
          best_ranking_position: top10Courses.length > 0 ? Math.min(...top10Courses.map(r => r.ranking_position)) : 0,
          recent_achievements_count: 0
        }
      };

      return this.handleResponse(summary);
    } catch (error) {
      console.error('💥 getUserPerformanceZones error:', error);
      return this.handleResponse(null, error);
    }
  }

  // Get achievements that user is close to earning
  async getProgressToTop10(userId: string): Promise<ServiceResponse<ProgressUpdate[]>> {
    try {
      const progressUpdates: ProgressUpdate[] = [];

      // Get user's recent rounds to analyze potential improvements
      const { data: recentRounds } = await this.supabase
        .from('solo_rounds')
        .select(`
          id,
          course_id,
          total_score,
          par_differential,
          completed_at,
          courses!inner(name)
        `)
        .eq('user_id', userId)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false })
        .limit(10);

      if (!recentRounds || recentRounds.length === 0) {
        return this.handleResponse(progressUpdates);
      }

      // For each recent course, check how close user is to top 10
      for (const round of recentRounds) {
        // Check course top 10
        const courseTop10Result = await this.courseLeaderboard.getTop10ForCourse({
          course_id: round.course_id
        });

        if (courseTop10Result.success && courseTop10Result.data) {
          const top10 = courseTop10Result.data;
          const userBestScore = round.total_score;
          
          // If user isn't in top 10, check how close they are
          const userInTop10 = top10.find(entry => entry.user_id === userId);
          
          if (!userInTop10 && top10.length >= 10) {
            const worstTop10Score = top10[9].score; // 10th place score
            const strokesNeeded = Math.max(0, userBestScore - worstTop10Score + 1);
            
            if (strokesNeeded <= 5) { // Only show if within 5 strokes
              progressUpdates.push({
                badge_id: `course_top10_${round.course_id}`,
                badge_name: `Top 10 at ${round.courses?.name}`,
                current_progress: userBestScore,
                target_value: worstTop10Score,
                progress_percentage: Math.max(0, 1 - (strokesNeeded / 10)),
                next_milestone: `Need ${strokesNeeded} fewer strokes for Top 10`,
                tips: [
                  'Focus on eliminating big numbers',
                  'Practice course management',
                  'Work on putting to save strokes'
                ]
              });
            }
          }
        }

        // Check Local Legend opportunities
        const legendOpportunityResult = await this.localLegend.getUserActiveCourses(userId);
        if (legendOpportunityResult.success && legendOpportunityResult.data) {
          const opportunities = legendOpportunityResult.data
            .filter(course => !course.is_legend && course.rounds_to_legend <= 3);
          
          for (const opportunity of opportunities) {
            progressUpdates.push({
              badge_id: `local_legend_${opportunity.course_id}`,
              badge_name: `Local Legend at ${opportunity.course_name}`,
              current_progress: opportunity.user_round_count,
              target_value: opportunity.legend_round_count + 1,
              progress_percentage: opportunity.user_round_count / (opportunity.legend_round_count + 1),
              next_milestone: `Play ${opportunity.rounds_to_legend} more rounds`,
              tips: [
                'Play consistently at this course',
                'Maintain regular playing schedule',
                'Focus on course familiarity'
              ]
            });
          }
        }
      }

      // Sort by progress percentage (closest to achieving first)
      progressUpdates.sort((a, b) => b.progress_percentage - a.progress_percentage);

      return this.handleResponse(progressUpdates.slice(0, 5)); // Top 5 opportunities
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Get chronological achievement timeline
  async getAchievementTimeline(
    userId: string,
    limit: number = 50
  ): Promise<ServiceResponse<PerformanceAchievement[]>> {
    try {
      const { data, error } = await this.supabase
        .from('performance_achievements')
        .select(`
          *,
          courses!inner(name)
        `)
        .eq('user_id', userId)
        .order('achieved_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Transform data to include course name
      const achievements: PerformanceAchievement[] = data?.map(achievement => ({
        ...achievement,
        course_name: achievement.courses?.name
      })) || [];

      return this.handleResponse(achievements);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Get recent achievements (last 30 days)
  async getRecentAchievements(
    userId: string,
    limit: number = 10
  ): Promise<ServiceResponse<PerformanceAchievement[]>> {
    try {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

      const { data, error } = await this.supabase
        .from('performance_achievements')
        .select(`
          *,
          courses!inner(name)
        `)
        .eq('user_id', userId)
        .eq('is_current', true)
        .gte('achieved_at', thirtyDaysAgo)
        .order('achieved_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Transform data to include course name
      const achievements: PerformanceAchievement[] = data?.map(achievement => ({
        ...achievement,
        course_name: achievement.courses?.name
      })) || [];

      return this.handleResponse(achievements);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Notify user of record achievement
  async notifyRecordAchievement(
    userId: string, 
    achievement: PerformanceAchievement
  ): Promise<ServiceResponse<RecordNotification>> {
    try {
      // Get course name if not provided
      let courseName = achievement.course_name;
      if (!courseName && achievement.course_id) {
        const { data: course } = await this.supabase
          .from('courses')
          .select('name')
          .eq('id', achievement.course_id)
          .single();
        courseName = course?.name || 'Unknown Course';
      }

      // Create notification based on achievement type
      let notification: RecordNotification;

      switch (achievement.achievement_type) {
        case 'hole_record':
          notification = {
            type: 'new_record',
            title: `New ${achievement.achievement_title}!`,
            message: `You set a new ${this.getAchievementDisplayName(achievement.achievement_title)} on Hole ${achievement.hole_number}`,
            achievement_data: {
              course_name: courseName || 'Unknown Course',
              hole_number: achievement.hole_number,
              score: achievement.score_value
            },
            timestamp: achievement.achieved_at
          };
          break;

        case 'course_record':
          notification = {
            type: 'new_record',
            title: `New ${achievement.achievement_title}!`,
            message: `You set a new ${this.getAchievementDisplayName(achievement.achievement_title)} with a score of ${achievement.score_value}`,
            achievement_data: {
              course_name: courseName || 'Unknown Course',
              score: achievement.score_value
            },
            timestamp: achievement.achieved_at
          };
          break;

        case 'local_legend':
          notification = {
            type: 'local_legend',
            title: 'Local Legend!',
            message: `You are now the Local Legend at ${courseName}!`,
            achievement_data: {
              course_name: courseName || 'Unknown Course'
            },
            timestamp: achievement.achieved_at
          };
          break;

        case 'top_10':
          notification = {
            type: 'new_top_10',
            title: `Top ${achievement.ranking_position}!`,
            message: `You made it to #${achievement.ranking_position} ${achievement.hole_number ? `on Hole ${achievement.hole_number}` : 'on the course'}`,
            achievement_data: {
              course_name: courseName || 'Unknown Course',
              hole_number: achievement.hole_number,
              position: achievement.ranking_position,
              score: achievement.score_value
            },
            timestamp: achievement.achieved_at
          };
          break;

        default:
          notification = {
            type: 'new_record',
            title: 'New Achievement!',
            message: `You earned a new achievement: ${achievement.achievement_title}`,
            achievement_data: {
              course_name: courseName || 'Unknown Course'
            },
            timestamp: achievement.achieved_at
          };
      }

      // TODO: Here you could integrate with push notification service
      // await this.sendPushNotification(userId, notification);

      return this.handleResponse(notification);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Create a new achievement record
  async recordAchievement(
    userId: string,
    achievementData: {
      achievement_type: 'hole_record' | 'course_record' | 'local_legend' | 'top_10';
      achievement_title: 'KOH' | 'QOH' | 'HR' | 'KOC' | 'QOC' | 'CR' | 'LL' | 'T10';
      course_id: string;
      hole_number?: number;
      round_id?: string;
      score_value?: number;
      ranking_position?: number;
      record_type?: 'scratch' | 'net';
      gender?: string;
    }
  ): Promise<ServiceResponse<PerformanceAchievement>> {
    try {
      // Check if achievement already exists and is current
      let query = this.supabase
        .from('performance_achievements')
        .select('*')
        .eq('user_id', userId)
        .eq('achievement_type', achievementData.achievement_type)
        .eq('course_id', achievementData.course_id)
        .eq('is_current', true);

      if (achievementData.hole_number) {
        query = query.eq('hole_number', achievementData.hole_number);
      }

      const { data: existingAchievement } = await query.single();

      // If achievement exists, mark it as lost (not current)
      if (existingAchievement) {
        await this.supabase
          .from('performance_achievements')
          .update({
            is_current: false,
            lost_at: new Date().toISOString()
          })
          .eq('id', existingAchievement.id);
      }

      // Create new achievement record
      const { data, error } = await this.supabase
        .from('performance_achievements')
        .insert({
          user_id: userId,
          achievement_type: achievementData.achievement_type,
          achievement_title: achievementData.achievement_title,
          course_id: achievementData.course_id,
          hole_number: achievementData.hole_number,
          round_id: achievementData.round_id,
          score_value: achievementData.score_value,
          ranking_position: achievementData.ranking_position,
          record_type: achievementData.record_type,
          gender: achievementData.gender,
          achieved_at: new Date().toISOString(),
          is_current: true
        })
        .select(`
          *,
          courses!inner(name)
        `)
        .single();

      if (error) throw error;

      const achievement: PerformanceAchievement = {
        ...data,
        course_name: data.courses?.name
      };

      return this.handleResponse(achievement);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Get achievement statistics for user
  async getAchievementStats(userId: string): Promise<ServiceResponse<{
    total_achievements: number;
    achievements_by_type: Record<string, number>;
    achievements_by_month: Array<{ month: string; count: number }>;
    current_streaks: Array<{ type: string; courses: string[]; count: number }>;
  }>> {
    try {
      const { data: achievements, error } = await this.supabase
        .from('performance_achievements')
        .select('*')
        .eq('user_id', userId)
        .eq('is_current', true);

      if (error) throw error;

      const totalAchievements = achievements?.length || 0;

      // Group by type
      const achievementsByType: Record<string, number> = {};
      achievements?.forEach(achievement => {
        const type = achievement.achievement_type;
        achievementsByType[type] = (achievementsByType[type] || 0) + 1;
      });

      // Group by month (last 12 months)
      const achievementsByMonth: Array<{ month: string; count: number }> = [];
      const now = new Date();
      
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthKey = date.toISOString().substring(0, 7); // YYYY-MM format
        const monthName = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        
        const count = achievements?.filter(a => 
          a.achieved_at.substring(0, 7) === monthKey
        ).length || 0;
        
        achievementsByMonth.push({ month: monthName, count });
      }

      // Calculate current streaks (simplified)
      const currentStreaks: Array<{ type: string; courses: string[]; count: number }> = [];
      
      // Group achievements by type and count unique courses
      const typeGroups = achievements?.reduce((groups, achievement) => {
        const type = achievement.achievement_type;
        if (!groups[type]) groups[type] = new Set();
        groups[type].add(achievement.course_id);
        return groups;
      }, {} as Record<string, Set<string>>) || {};

      Object.entries(typeGroups).forEach(([type, courseSet]) => {
        if (courseSet.size >= 2) { // Only show if 2+ courses
          currentStreaks.push({
            type: this.getAchievementTypeDisplayName(type),
            courses: Array.from(courseSet).slice(0, 5), // Show first 5 courses
            count: courseSet.size
          });
        }
      });

      return this.handleResponse({
        total_achievements: totalAchievements,
        achievements_by_type: achievementsByType,
        achievements_by_month: achievementsByMonth,
        current_streaks: currentStreaks
      });
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Helper methods for display names
  private getAchievementDisplayName(title: string): string {
    const displayNames: Record<string, string> = {
      'KOH': 'King of Hole',
      'QOH': 'Queen of Hole',
      'HR': 'Hole Record',
      'KOC': 'King of Course',
      'QOC': 'Queen of Course',
      'CR': 'Course Record',
      'LL': 'Local Legend',
      'T10': 'Top 10'
    };
    return displayNames[title] || title;
  }

  private getAchievementTypeDisplayName(type: string): string {
    const displayNames: Record<string, string> = {
      'hole_record': 'Hole Records',
      'course_record': 'Course Records',
      'local_legend': 'Local Legends',
      'top_10': 'Top 10 Finishes'
    };
    return displayNames[type] || type;
  }
}